import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import MembershipSellModal from '../components/MembershipSellModal';
import { validateSession } from '../utils/functions';
import { navigateTo } from '../utils/navigation';
import {
    memberDetail, sellMembership, cancelMember, listPlans, quoteChangePlan, changePlan,
    ESTADO_MEMBRESIA, ESTADO_SOCIO, METODO_PAGO_MEMBRESIA, formatYMD, textoBeneficios,
} from '../utils/membershipsApi';
import { formatEventDateTime } from '../utils/formats';
import { loadPosDevices, posDeviceField } from '../utils/posDevices';
import PosDeviceSelect from '../components/PosDeviceSelect';

const METODOS_COBRO = ['efectivo', 'pos', 'transferencia', 'mercadopago'];

const ROLE_ADMIN = 1;

// `created_at` / `cancelled_at` los escribe el backend con `new Date()`: llegan en UTC CON zona
// ('…Z'), así que acá sí corresponde pasarlos por Date y mostrarlos en la hora del local.
function fechaHora(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString('es-UY', {
        timeZone: 'America/Montevideo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

const pesos = (n) => `$ ${Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })}`;

// Detalle del socio: la membresía es UNA y se va renovando. Acá se ven sus períodos (cada venta
// o renovación, con su pago), las entradas de socio que sacó, y vive la baja — que es rara y
// destructiva, así que está al final, sólo para el admin, y pide motivo + la cédula tipeada.
class MembershipDetailPage extends Component {

    constructor(props) {
        super(props);
        const cedula = new URLSearchParams(props.location.search).get('cedula') || '';
        this.state = {
            checked: !!localStorage.getItem('token'),
            role: Number(localStorage.getItem('role') || 0),
            cedula,
            loading: true,
            error: null,
            data: null,
            form: false,
            aviso: null,
            bajaAbierta: false,
            motivo: '',
            confirmaCedula: '',
            dandoDeBaja: false,
            plans: [],
            // Cambio de plan en el momento: plan elegido, su cotización y el medio de pago.
            cambio: null, // { planId, quote, loading, error, paymentMethod, posDeviceId, saving }
            // Los lectores de tarjeta, para cobrar la diferencia con POS (spec-caja D-03).
            posDevices: [],
            posDeviceDefault: '',
        };
    }

    async componentDidMount() {
        const role = await validateSession(true);
        this.setState({ checked: true, role });
        this.load();
        loadPosDevices().then(({ posDevices, posDeviceId }) => this.setState({ posDevices, posDeviceDefault: posDeviceId }));
        try {
            const data = await listPlans({ includeInactive: true });
            this.setState({ plans: (data && data.plans) || [] });
        } catch (e) { /* los beneficios del resumen y el cambio de plan quedan sin datos */ }
    }

    // --- Cambio de plan ---
    elegirPlanNuevo = async (planId) => {
        if (!planId) { this.setState({ cambio: null }); return; }
        this.setState({ cambio: { planId, quote: null, loading: true, error: null, paymentMethod: 'efectivo', posDeviceId: this.state.posDeviceDefault } });
        try {
            const data = await quoteChangePlan(this.state.data.member.cedula, planId);
            // Una respuesta de un plan elegido antes no pisa la del elegido ahora.
            if (!this.state.cambio || String(this.state.cambio.planId) !== String(planId)) return;
            this.setState({ cambio: { ...this.state.cambio, quote: data.quote, loading: false } });
        } catch (e) {
            if (!this.state.cambio || String(this.state.cambio.planId) !== String(planId)) return;
            this.setState({ cambio: { ...this.state.cambio, loading: false, error: e.message } });
        }
    }

    confirmarCambio = async () => {
        const { cambio, data } = this.state;
        if (!cambio || !cambio.quote || cambio.saving) return;
        const q = cambio.quote;
        // Cortesía: el cambio no se cobra (sin venta ni caja). Sólo tiene sentido si había algo que cobrar.
        const cortesia = !!cambio.courtesy && q.amount > 0;
        this.setState({ cambio: { ...cambio, saving: true, error: null } });
        try {
            const r = await changePlan(data.member.cedula, {
                planId: q.toPlanId,
                // El backend rechaza (QUOTE_CHANGED) si el importe ya no es el que vio el operador.
                expectedAmount: q.amount,
                ...(cortesia ? { courtesy: true } : {}),
                ...(q.amount > 0 && !cortesia ? { paymentMethod: cambio.paymentMethod } : {}),
                ...posDeviceField(q.amount > 0 && !cortesia && cambio.paymentMethod === 'pos', cambio.posDeviceId || this.state.posDeviceDefault),
            });
            const cobrado = r.quote.courtesy
                ? `Sin cobro: cortesía (la diferencia era de $${r.quote.amount}).`
                : (r.quote.amount > 0 ? `Se cobraron $${r.quote.amount}.` : 'Sin cobro.');
            const reservas = r.seatsConverted
                ? ` ${r.seatsConverted === 1 ? 'Una reserva futura' : `${r.seatsConverted} reservas futuras`} con lugar sin cargo pasó a cobrar la entrada (el plan nuevo no cubre ese día).`
                : '';
            this.setState({ cambio: null, aviso: `Plan cambiado a ${q.toPlanName} desde hoy, vence el ${formatYMD(q.validUntil)}. ${cobrado}${reservas}` }, this.load);
        } catch (e) {
            this.setState({ cambio: { ...this.state.cambio, saving: false, error: e.message } });
            // Cotización vieja: se pide de nuevo para mostrar el importe vigente.
            if (/cambió/i.test(String(e.message || ''))) this.elegirPlanNuevo(cambio.planId);
        }
    }

    load = async () => {
        this.setState({ loading: true, error: null });
        try {
            const data = await memberDetail(this.state.cedula);
            this.setState({ data, loading: false });
        } catch (e) {
            this.setState({ error: e.message, loading: false });
        }
    }

    vender = async (payload) => {
        const data = await sellMembership(payload);
        const m = data.membership;
        const texto = data.renewedFrom
            ? `Renovación registrada: del ${formatYMD(m.valid_from)} al ${formatYMD(m.valid_until)} (la anterior vencía el ${formatYMD(data.renewedFrom)}).`
            : `Membresía registrada: del ${formatYMD(m.valid_from)} al ${formatYMD(m.valid_until)}.`;
        this.setState({ form: false, aviso: texto }, this.load);
    }

    darDeBaja = async () => {
        const { data, motivo } = this.state;
        const nombre = this.nombre();
        if (!window.confirm(`Última confirmación: dar de baja a ${nombre}.\n\nSe cortan el período en curso y todos los que ya pagó hacia adelante. No devuelve dinero y no se puede deshacer desde el sistema.`)) return;
        this.setState({ dandoDeBaja: true });
        try {
            const r = await cancelMember(data.member.cedula, motivo.trim());
            this.setState({
                dandoDeBaja: false, bajaAbierta: false, motivo: '', confirmaCedula: '',
                aviso: `${nombre} quedó dado de baja (${r.cancelled === 1 ? '1 período cortado' : `${r.cancelled} períodos cortados`}).`,
            }, this.load);
        } catch (e) {
            this.setState({ dandoDeBaja: false });
            alert(e.message);
        }
    }

    nombre() {
        const m = this.state.data && this.state.data.member;
        if (!m) return this.state.cedula;
        return (m.user && `${m.user.firstName || ''} ${m.user.lastName || ''}`.trim()) || m.cedula;
    }

    renderResumen() {
        const m = this.state.data.member;
        const estado = ESTADO_SOCIO[m.state] || { label: m.state, badge: 'badge-secondary' };
        let cobertura = null;
        if (m.state === 'vigente') cobertura = `Cubierto hasta el ${formatYMD(m.coveredUntil)}`;
        else if (m.state === 'futura') cobertura = `Empieza el ${formatYMD(m.nextFrom)} · cubre hasta el ${formatYMD(m.coveredUntil)}`;
        else if (m.state === 'vencida') cobertura = `Venció el ${formatYMD(m.lastUntil)}`;
        else cobertura = 'Todos sus períodos están dados de baja';
        return (
            <div className="card card-small mb-4">
                <div className="card-body">
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
                        <span className={'badge ' + estado.badge} style={{ fontSize: 13 }}>{estado.label}</span>
                        <b>{cobertura}</b>
                    </div>
                    <div className="row mt-3" style={{ fontSize: 13 }}>
                        <div className="col-md-3"><small className="text-muted d-block">Cédula</small>{m.cedula}</div>
                        <div className="col-md-3"><small className="text-muted d-block">Plan</small>{m.planName || '—'}
                            {this.planDe(m.planId) ? <small className="text-muted d-block">{textoBeneficios(this.planDe(m.planId))}</small> : null}
                        </div>
                        <div className="col-md-3"><small className="text-muted d-block">Socio desde</small>{m.memberSince ? formatYMD(m.memberSince) : '—'}</div>
                        <div className="col-md-3"><small className="text-muted d-block">Contacto</small>
                            {(m.user && m.user.email) || '—'}{m.user && m.user.phone ? <><br />{m.user.phone}</> : null}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    planDe(id) {
        return (this.state.plans || []).find((p) => Number(p.id) === Number(id)) || null;
    }

    // Cambiar el plan HOY: el vencimiento no se mueve; se cobra la diferencia proporcional a los
    // días que quedan; bajar a un plan más barato no cobra ni devuelve (decisión del dueño).
    renderCambioPlan() {
        const m = this.state.data.member;
        if (m.state !== 'vigente') return null;
        const opciones = (this.state.plans || []).filter((p) => p.active && Number(p.id) !== Number(m.planId));
        if (!opciones.length) return null;
        const cambio = this.state.cambio;
        const q = cambio && cambio.quote;
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom"><h6 className="m-0">Cambiar plan</h6></div>
                <div className="card-body" style={{ fontSize: 13 }}>
                    <div className="form-group mb-2" style={{ maxWidth: 420 }}>
                        <select className="form-control" value={(cambio && cambio.planId) || ''}
                            onChange={(e) => this.elegirPlanNuevo(e.target.value)}>
                            <option value="">Pasar a…</option>
                            {opciones.map((p) => (
                                <option key={p.id} value={p.id}>{p.name} — {pesos(p.price)} · {textoBeneficios(p)}</option>
                            ))}
                        </select>
                    </div>
                    {cambio && cambio.loading ? <p className="text-muted mb-0">Calculando…</p> : null}
                    {q ? (
                        <div style={{ maxWidth: 520 }}>
                            <p className="mb-2">
                                Pasa de <b>{q.fromPlanName}</b> a <b>{q.toPlanName}</b> desde hoy y sigue venciendo
                                el <b>{formatYMD(q.validUntil)}</b>.
                            </p>
                            {q.amount > 0 ? (
                                <>
                                    <p className="mb-2">
                                        {cambio.courtesy
                                            ? <>Diferencia: <b style={{ fontSize: 16, textDecoration: 'line-through' }}>{pesos(q.amount)}</b> <b>(no se cobra)</b></>
                                            : <>A cobrar: <b style={{ fontSize: 16 }}>{pesos(q.amount)}</b></>}
                                        <span className="text-muted"> — la diferencia ({pesos(q.newPrice - q.oldPrice)}) por los {q.remainingDays} de {q.periodDays} días que le quedan.</span>
                                    </p>
                                    {/* Cortesía: el cambio queda gratis (sin venta ni caja), igual que regalar una membresía. */}
                                    <label className="d-flex align-items-center mb-2" style={{ gap: 6, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={!!cambio.courtesy}
                                            onChange={(e) => this.setState({ cambio: { ...cambio, courtesy: e.target.checked } })} />
                                        Sin cobrar (cortesía)
                                    </label>
                                    {!cambio.courtesy && (
                                        <div className="form-group mb-2" style={{ maxWidth: 260 }}>
                                            <select className="form-control form-control-sm" value={cambio.paymentMethod}
                                                onChange={(e) => this.setState({ cambio: { ...cambio, paymentMethod: e.target.value } })}>
                                                {METODOS_COBRO.map((k) => <option key={k} value={k}>{METODO_PAGO_MEMBRESIA[k]}</option>)}
                                            </select>
                                            {cambio.paymentMethod === 'pos' && (
                                                <PosDeviceSelect className="form-control form-control-sm mt-2" devices={this.state.posDevices}
                                                    value={cambio.posDeviceId || this.state.posDeviceDefault}
                                                    onChange={(posDeviceId) => this.setState({ cambio: { ...this.state.cambio, posDeviceId } })} />
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p className="mb-2 text-muted">
                                    Sin cobro{q.downgrade ? ': es un plan más barato y no hay devolución.' : '.'}
                                </p>
                            )}
                            <button type="button" className="btn btn-sm btn-accent mr-2" disabled={cambio.saving} onClick={this.confirmarCambio}>
                                {cambio.saving ? 'Cambiando…' : q.amount > 0 && !cambio.courtesy ? `Cobrar ${pesos(q.amount)} y cambiar` : q.amount > 0 ? 'Cambiar sin cobrar' : 'Cambiar plan'}
                            </button>
                            <button type="button" className="btn btn-sm btn-white" onClick={() => this.setState({ cambio: null })}>Cancelar</button>
                        </div>
                    ) : null}
                    {cambio && cambio.error ? <div className="alert alert-danger py-2 mt-2 mb-0">{cambio.error}</div> : null}
                </div>
            </div>
        );
    }

    // Visitas del socio: cada vez que se sentó en una mesa o le validaron la entrada al club.
    renderVisitas() {
        const v = this.state.data.visits;
        if (!v) return null;
        const periodo = v.period;
        const lista = v.list || [];
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom"><h6 className="m-0">Visitas</h6></div>
                <div className="card-body" style={{ fontSize: 13 }}>
                    <div className="row mb-2">
                        <div className="col-md-3"><small className="text-muted d-block">En el período actual</small>
                            {periodo ? <b>{periodo.total} {periodo.total === 1 ? 'día' : 'días'}</b> : '—'}
                            {periodo ? <small className="text-muted d-block">{periodo.mesa} a mesa · {periodo.club} al club</small> : null}
                        </div>
                        <div className="col-md-3"><small className="text-muted d-block">Lugares sin cargo usados</small>
                            {periodo ? periodo.freeSeats : '—'}
                        </div>
                        <div className="col-md-3"><small className="text-muted d-block">Desde siempre</small>
                            {v.allTime.total} {v.allTime.total === 1 ? 'día' : 'días'}
                        </div>
                        <div className="col-md-3"><small className="text-muted d-block">Última visita</small>
                            {v.allTime.last ? formatYMD(v.allTime.last) : '—'}
                        </div>
                    </div>
                    {!lista.length ? (
                        <span className="text-muted">Todavía no hay visitas registradas.</span>
                    ) : (
                        <ul className="mb-0 pl-3">
                            {lista.slice(0, 30).map((x, i) => (
                                <li key={`${x.date}-${x.kind}-${x.sourceId}-${i}`}>
                                    {formatYMD(x.date)} · {x.kind === 'club' ? 'Club' : 'Mesa'}
                                    <span className="text-muted">{x.free ? (x.kind === 'club' ? ' · entrada de socio' : ' · sin cargo') : ' · pagó'}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <small className="text-muted d-block mt-2">
                        Antes de octubre de 2026 sólo están las entradas al club validadas y las mesas que reservó a su nombre.
                    </small>
                </div>
            </div>
        );
    }

    renderPeriodos() {
        const periods = this.state.data.periods || [];
        // Un período que reemplazó un cambio de plan no "venció": se cortó por el cambio.
        const reemplazados = new Set(periods.map((p) => p.changed_from_id).filter(Boolean).map(Number));
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom"><h6 className="m-0">Períodos y pagos</h6></div>
                <div className="card-body p-0 pb-3">
                    <div className="dataTables_wrapper no-footer">
                        <table className="transaction-history dataTable no-footer dtr-inline" role="grid">
                            <thead>
                                <tr role="row">
                                    <th style={{ width: '11%' }}>Desde</th>
                                    <th style={{ width: '11%' }}>Hasta</th>
                                    <th className="text-left" style={{ width: '15%' }}>Plan</th>
                                    <th style={{ width: '14%' }}>Estado</th>
                                    <th style={{ width: '9%' }}>Precio</th>
                                    <th style={{ width: '18%' }}>Pago</th>
                                    <th className="text-left" style={{ width: '22%' }}>Registrado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {periods.map((p, i) => {
                                    const estado = reemplazados.has(Number(p.id)) && p.state !== 'cancelada'
                                        ? { label: 'Cambió de plan', badge: 'badge-light' }
                                        : (ESTADO_MEMBRESIA[p.state] || { label: p.state, badge: 'badge-secondary' });
                                    return (
                                        <tr role="row" key={p.id} style={{ background: i % 2 === 0 ? '#ffffff' : '#fcfcfc' }}>
                                            <td>{formatYMD(p.valid_from)}</td>
                                            <td>{formatYMD(p.valid_until)}</td>
                                            <td className="text-left">{p.plan_name}{p.changed_from_id ? <small className="text-muted d-block">cambio de plan</small> : null}</td>
                                            <td><span className={'badge ' + estado.badge}>{estado.label}</span></td>
                                            <td>{Number(p.price) === 0 ? (p.changed_from_id ? 'Sin cobro' : 'Cortesía') : pesos(p.price)}</td>
                                            <td>
                                                {METODO_PAGO_MEMBRESIA[p.payment_method] || p.payment_method || '—'}
                                                {p.purchase_id ? <small className="text-muted d-block">venta #{p.purchase_id}</small> : null}
                                            </td>
                                            <td className="text-left" style={{ fontSize: 12 }}>
                                                {fechaHora(p.created_at)}{p.created_by ? <span className="text-muted"> · {p.created_by}</span> : null}
                                                {p.status === 'cancelled' ? (
                                                    <div className="text-danger">
                                                        Baja {fechaHora(p.cancelled_at)}{p.cancelled_by ? ` · ${p.cancelled_by}` : ''}
                                                        {p.cancel_reason ? <div className="text-muted">Motivo: {p.cancel_reason}</div> : null}
                                                    </div>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    renderEntradas() {
        const tickets = this.state.data.memberTickets || [];
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom"><h6 className="m-0">Entradas de socio</h6></div>
                <div className="card-body" style={{ fontSize: 13 }}>
                    {!tickets.length ? (
                        <span className="text-muted">Todavía no sacó ninguna entrada de socio.</span>
                    ) : (
                        <ul className="mb-0 pl-3">
                            {tickets.map((t) => (
                                <li key={t.id}>
                                    {t.eventTitle || 'Evento'} · {formatEventDateTime(t.startsAt)}
                                    <span className="text-muted"> · {t.status === 'redeemed' ? 'ingresó' : t.status === 'valid' ? 'válida' : t.status}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        );
    }

    // La baja: sólo admin, sólo si hay algo vigente o futuro que cortar, y detrás de tres
    // barreras (abrir el panel, motivo + cédula tipeada, confirm final).
    renderBaja() {
        const m = this.state.data.member;
        if (this.state.role !== ROLE_ADMIN || !['vigente', 'futura'].includes(m.state)) return null;
        const listo = this.state.motivo.trim().length >= 5
            && this.state.confirmaCedula.replace(/\D/g, '') === String(m.cedula);
        return (
            <div className="card card-small mb-4" style={{ borderTop: '3px solid #c4183c' }}>
                <div className="card-body">
                    <h6 className="text-danger">Dar de baja al socio</h6>
                    <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                        Es para casos raros: una transferencia que nunca llegó, un alta equivocada. Corta el
                        período en curso y todos los que ya pagó hacia adelante; los terminados quedan como
                        historia. <b>No devuelve dinero</b> y queda registrado quién lo hizo y por qué.
                    </p>
                    {!this.state.bajaAbierta ? (
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => this.setState({ bajaAbierta: true })}>
                            Dar de baja…
                        </button>
                    ) : (
                        <div style={{ maxWidth: 520 }}>
                            <div className="form-group">
                                <label style={{ fontSize: 13 }}>Motivo (queda registrado)</label>
                                <textarea className="form-control" rows="2" value={this.state.motivo}
                                    onChange={(e) => this.setState({ motivo: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label style={{ fontSize: 13 }}>Para confirmar, escribí la cédula del socio ({m.cedula})</label>
                                <input type="text" className="form-control" value={this.state.confirmaCedula}
                                    onChange={(e) => this.setState({ confirmaCedula: e.target.value })} />
                            </div>
                            <button type="button" className="btn btn-sm btn-danger mr-2" disabled={!listo || this.state.dandoDeBaja}
                                onClick={this.darDeBaja}>
                                {this.state.dandoDeBaja ? 'Dando de baja…' : 'Dar de baja al socio'}
                            </button>
                            <button type="button" className="btn btn-sm btn-white"
                                onClick={() => this.setState({ bajaAbierta: false, motivo: '', confirmaCedula: '' })}>
                                Cancelar
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        if (!this.state.checked) return (<div></div>);
        const { data } = this.state;
        return (
            <div className="container-fluid">
                {this.state.form && data ? (
                    <MembershipSellModal
                        renovar={{ cedula: data.member.cedula, planId: data.member.planId }}
                        onCancel={() => this.setState({ form: false })}
                        onSell={this.vender} />
                ) : null}
                <div className="row">
                    <SidebarComponent itemSelected={49} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                    <HeaderComponent title="Membresías" subtitle={this.nombre()} />
                                </div>
                                <div className="text-nowrap" style={{ flex: '0 0 auto' }}>
                                    <button type="button" className="btn btn-white" onClick={() => navigateTo('/memberships')}>
                                        Volver a membresías
                                    </button>
                                    {data ? (
                                        <button type="button" className="btn btn-accent ml-2" onClick={() => this.setState({ form: true, aviso: null })}>
                                            Renovar
                                        </button>
                                    ) : null}
                                </div>
                            </div>

                            {this.state.aviso ? <div className="alert alert-success" role="alert">{this.state.aviso}</div> : null}
                            {this.state.loading && !data ? <p className="text-muted">Cargando…</p> : null}
                            {this.state.error ? <div className="alert alert-danger" role="alert">{this.state.error}</div> : null}
                            {data ? (
                                <>
                                    {this.renderResumen()}
                                    {this.renderCambioPlan()}
                                    {this.renderVisitas()}
                                    {this.renderPeriodos()}
                                    {this.renderEntradas()}
                                    {this.renderBaja()}
                                </>
                            ) : null}
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

export default MembershipDetailPage;
